// externals
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';

// components
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { DatePicker } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputSize } from '@interstate/components/TextInput';

// selectors
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as dealLifeCycleActionCreators from '@makemydeal/dr-dash-store/actions/dealLifeCycleActionCreators';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import { dealLockHooks } from '../hooks';

// styles
import { StyledDateLabel } from './DealLifecycleModal.style';

// consts/enums
import { DealLifeCycleStatus, DELIVERED, DELIVERY_DATE, DATE_FORMAT } from '../constants';

interface DeliveredDateFieldProps {
    id: string;
    value: string;
    onChange: (e: InterstateOnChangeEvent<DatePickerEventValue>, type: string) => void;
    dealStatusValue: string;
    showStyledLabel?: boolean;
    size?: TextInputSize;
}

export const DeliveredDateField: React.FC<DeliveredDateFieldProps> = ({
    id,
    value,
    onChange,
    dealStatusValue,
    showStyledLabel = false,
    size = 'medium'
}) => {
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const maxAllowedFutureMonthsInDateField = useSelector(configSelectors.getMaxAllowedFutureMonthsInDateField);
    const dealLifeCycle = useSelector(dealLifecycleSelectors.getDealLifecycle);
    const currentDeliveredDate = dealLifeCycle.deliveredDate || '';
    const currentDate = new Date();
    const maxAllowedDate = new Date(currentDate.setMonth(currentDate.getMonth() + maxAllowedFutureMonthsInDateField));
    const oneYearAgoFromNow = new Date(currentDate.setFullYear(currentDate.getFullYear() - 1));

    const isFieldEmpty = (fieldValue: string): boolean => {
        return fieldValue === '';
    };

    const getDateFieldDisabledState = useMemo(
        () =>
            (dealStatusValue === DealLifeCycleStatus.IN_PROGRESS || dealStatusValue === DealLifeCycleStatus.LOST) &&
            isFieldEmpty(currentDeliveredDate || ''),
        [dealStatusValue, currentDeliveredDate]
    );

    const handleOpenModal = useCallback(() => {
        dispatch(dealLifeCycleActionCreators.showDealLifeCycleModal(true));
    }, [dispatch]);

    return (
        <>
            {showStyledLabel && (
                <StyledDateLabel data-testid={`${id}-label`} onClick={handleOpenModal}>
                    {DELIVERY_DATE}
                </StyledDateLabel>
            )}
            <DatePicker
                id={id}
                data-testid={id}
                label={DELIVERY_DATE}
                dateFormat={DATE_FORMAT}
                value={value ? new Date(value) : null}
                onChange={(e) => onChange(e, DELIVERED)}
                disabled={isLocked || getDateFieldDisabledState}
                required={false}
                hasError={false}
                locale="en-US"
                maxDate={maxAllowedDate}
                minDate={oneYearAgoFromNow}
                displayLabel={!showStyledLabel}
                size={size}
            />
        </>
    );
};
